import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Color,
  Elevation,
  FontWeight,
  Radius,
  Spacing,
  content,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { useCopyToClipboard, useTimeoutFn } from "@lkf/hooks";
import Button from "@ui-kit/button";
import Icon from "@ui-kit/icon";
import UiKitMarkdown from "@ui-kit/markdown";
import Tooltip from "@ui-kit/tooltip";
import { useFeature } from "@radarly/core/features/use-cases";
import { tr } from "@radarly/i18n";

export const AI_GRADIENT = { start: "#B627A1", stop: "#1D9F9F" };

const Banner = styled.div<{
  hidden: boolean;
  showCopy: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Small};
  color: ${content};
  background-color: ${Color.White};
  border-radius: ${Radius.Medium};

  position: relative;
  min-height: 50px;
  margin-bottom: ${Spacing.MediumLarge};
  line-height: 22px;

  padding: ${Spacing.MediumLarge};

  transition-property: min-height, line-height, padding-top, padding-bottom,
    margin, opacity;
  transition-duration: 0.3s;

  background: ${Color.White};

  box-shadow: ${Elevation.Elevation_1};

  ${({ hidden }) =>
    hidden &&
    `
  min-height: 0;
  line-height: 0;
  padding: 0;
  margin: 0;
  opacity: 0;
  `}

  &:before {
    pointer-events: none;
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    border: 2px solid transparent;
    border-radius: ${Radius.Medium};
    background: linear-gradient(to bottom, ${Color.White}, ${Color.White})
        content-box,
      linear-gradient(to right, ${AI_GRADIENT.start}, ${AI_GRADIENT.stop})
        border-box;
    mix-blend-mode: multiply;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Title = styled.div`
  display: flex;
  font-weight: ${FontWeight.Bold};
  color: ${AI_GRADIENT.start};
  background: var(
    --AI-Stroke,
    linear-gradient(90deg, ${AI_GRADIENT.start} 0%, ${AI_GRADIENT.stop} 100%)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  align-items: center;
  gap: ${Spacing.Small};
`;

const Actions = styled.div`
  display: flex;

  button {
    margin-top: -${Spacing.Small};
    margin-bottom: -${Spacing.Small};
    position: relative;
    left: ${Spacing.Small};
  }
`;

const Content = styled.div<{ hidden: boolean; isSpeakingEnded: boolean }>`
  transition: opacity 0.2s;
  transition-delay: 0.3s;
  font-size: 16px;

  ul {
    list-style: disc;
    margin-left: ${Spacing.Large};
  }

  li {
    line-height: 1.2em;
    margin-bottom: 6px;
  }

  & > ul > li:last-child:after,
  & > *:not(ul):last-child:after {
    content: "";
    display: inline;
    margin-left: 3px;
    animation: ${(props) =>
      props.isSpeakingEnded ? "none" : "blink 1s step-end infinite"};
    border-right: 4px solid transparent;
    height: 1em;
  }

  ${(props) =>
    props.hidden &&
    `
  opacity: 0;
  transition: opacity 0s;
  transition-delay: 0s;
  pointer-events: none;
  `}
`;

const useWrite = () => {
  const [text, setText] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSpeakingEnded, setIsSpeakingEnded] = useState(false);

  const write = useCallback((text: string, isLastWrite = false) => {
    let timeout: number;
    let letterCounter = 0;

    const addLetter = () => {
      setText(text.slice(0, letterCounter));
      letterCounter++;

      if (text.charAt(letterCounter - 1) === " ") {
        setIsSpeaking(false);
      } else {
        if (letterCounter % 3 === 0) {
          setIsSpeaking((isSpeaking) => !isSpeaking);
        }
      }
      if (letterCounter <= text.length) {
        timeout = window.setTimeout(
          addLetter,
          text.charAt(letterCounter - 1) === " " ? 80 : 5
        );
      } else {
        setIsSpeaking(false);
        if (isLastWrite) setIsSpeakingEnded(true);
      }
    };

    addLetter();

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return { write, text, isSpeaking, isSpeakingEnded };
};

const usePreambule = (
  locale: Locale,
  show: boolean,
  write: (s: string) => void
) => {
  const [isReady, cancel, reset] = useTimeoutFn(() => {
    write(tr(locale, "DISCOVERY::AICHA::LOADING_PLACEHOLDER"));
  }, 300);
  const readyState = isReady();

  // Wait for Aicha box to be opened
  useEffect(() => {
    // Only cancel immediately if Explanation starts closed
    if (!show && readyState === null) {
      cancel();
    }
  }, [show, cancel, readyState]);

  // Opened, start the "thinking" text
  useEffect(() => {
    if (show && readyState === null) {
      reset();
    }
  }, [show, readyState, reset]);

  return { readyState };
};

type IProps = {
  explanation: string;
  show?: boolean;
  onOpen?: (isOpen: boolean) => void;
  showCopy?: boolean;
  noCloseButton?: boolean;
  locale: Locale;
};

export const TellMeAicha = ({
  explanation,
  show = true,
  onOpen,
  showCopy,
  noCloseButton,
  locale,
}: IProps) => {
  const isAichaMarkdown = useFeature("aicha_markdown");
  const [hasLoaded, setHasLoaded] = useState(false);
  const { write, text, isSpeakingEnded } = useWrite();
  const [, copyToClipboard] = useCopyToClipboard();

  const { readyState } = usePreambule(locale, show, write);

  useEffect(() => {
    if (readyState && explanation && !hasLoaded) {
      setHasLoaded(true);
      setTimeout(() => {
        write(explanation, true);
      }, 2000);
    }
  }, [readyState, explanation, hasLoaded, write]);

  return (
    <Banner hidden={!show} showCopy={showCopy}>
      <Header>
        <Title>
          <Icon glyph="magic" size="s" />
          {tr(locale, "INSIGHT_PAGES::AICHA::EXPLANATION_TITLE")}
        </Title>
        <Actions>
          {showCopy && isSpeakingEnded && (
            <Tooltip
              tooltip={tr(locale, "INSIGHT_PAGES::AICHA::COPY_TEXT_TOOLTIP")}
              disabled={!explanation}
            >
              <Button
                icon="boxes"
                onClick={
                  explanation ? () => copyToClipboard(explanation) : null
                }
                size="s"
                variant="tertiary"
                circular
              />
            </Tooltip>
          )}
          {!noCloseButton && (
            <Button
              icon="cross"
              onClick={() => {
                onOpen?.(false);
              }}
              size="s"
              variant="tertiary"
              iconSize={12}
              circular
            />
          )}
        </Actions>
      </Header>
      <Content hidden={!show} isSpeakingEnded={isSpeakingEnded}>
        {isAichaMarkdown ? <UiKitMarkdown md={text} /> : text}
      </Content>
    </Banner>
  );
};
